<template>
  <v-menu
    :data-testid="`${testIdPrefix}__subscriptions-menu`"
    :value="value"
    @input="$emit('input', $event)"
    offset-x
    :nudge-right="0"
    transition="fade-transition"
    :close-on-content-click="closeOnContentClick"
    :open-delay="hoverOpenDelay"
    :close-delay="hoverCloseDelay"
    :content-class="'subscriptions-menu ' + contentClass"
    open-on-hover
  >
    <template #activator="{ on, attrs }">
      <v-list-item
        :data-testid="`${testIdPrefix}__subscriptions-activator`"
        v-bind="attrs"
        v-on="on"
        :class="activatorClasses"
        :aria-label="activatorAriaLabel"
      >
        <template v-if="mini">
          <v-tooltip
            :data-testid="`${testIdPrefix}__subscriptions-tooltip`"
            right
            content-class="tooltip-right"
          >
            <template #activator="{ on: ton, attrs: tattrs }">
              <v-list-item-icon
                :data-testid="`${testIdPrefix}__subscriptions-icon`"
                class="mr-2 ma-0 d-flex align-center justify-center"
                v-bind="tattrs"
                v-on="ton"
              >
                <img :src="iconSrc" :alt="iconAlt" class="nav-img-icon" />
              </v-list-item-icon>
            </template>
            <span :data-testid="`${testIdPrefix}__subscriptions-tooltip-text`">{{ label }}</span>
          </v-tooltip>
        </template>
        <template v-else>
          <v-list-item-icon
            :data-testid="`${testIdPrefix}__subscriptions-icon`"
            class="mr-2 ma-0 d-flex align-center justify-center"
          >
            <img :src="iconSrc" :alt="iconAlt" class="nav-img-icon" />
          </v-list-item-icon>
          <v-list-item-content
            :data-testid="`${testIdPrefix}__subscriptions-content`"
            class="pa-0 d-flex align-center justify-start"
          >
            <v-list-item-title
              :data-testid="`${testIdPrefix}__subscriptions-title`"
              class="side-nav-text-color hide-when-mini text-truncate"
            >{{ label }}</v-list-item-title>
          </v-list-item-content>
          <img
            v-if="!mini"
            src="/img/navicon/LeftArrow.svg"
            class="left-arrow-icon"
            :alt="`Left arrow for ${label}`"
          />
        </template>
      </v-list-item>
    </template>
    <v-card
      :data-testid="`${testIdPrefix}__subscriptions-card`"
      flat
      class="pa-0"
      width="240"
    >
      <v-list
        :data-testid="`${testIdPrefix}__subscriptions-list`"
        class="d-flex flex-column gap-1"
      >
        <v-list-item
          v-for="subItem in applist"
          :key="`${subItem.name || subItem.displayname || 'app'}-${keySuffix}`"
          :data-testid="`${testIdPrefix}__subscription-item`"
          @click="handleAppClick(subItem)"
          :class="listItemClasses(subItem)"
        >
          <v-list-item-title
            :data-testid="`${testIdPrefix}__subscription-title`"
            :class="labelClass"
          >{{ displayName(subItem) }}</v-list-item-title>
          <!-- <template v-if="showShortcutIcon">
            <v-progress-circular
              v-if="isShortcutLoading(subItem)"
              indeterminate
              size="16"
              width="2"
              color="primary"
              class="ml-2"
            />
            <img
              v-else
              :src="shortcutIconSrc"
              :class="shortcutIconClass"
              :alt="shortcutIconAlt"
              @click.stop.prevent="handleAddShortcut(subItem)"
            />
          </template>
          <template v-else-if="showTrailingShortcutIcon">
            <img
              :src="shortcutIconSrc"
              :class="trailingIconClass"
              :alt="shortcutIconAlt"
            />
          </template> -->
        </v-list-item>
        <v-list-item
          v-if="!hasApps"
          :data-testid="`${testIdPrefix}__subscriptions-empty`"
          disabled
        >
          <v-list-item-title
            :data-testid="`${testIdPrefix}__subscriptions-empty-text`"
            class="grey--text"
          >No apps</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: 'SubscriptionsMenuFlyout',
  props: {
    value: { type: Boolean, default: false },
    mini: { type: Boolean, default: false },
    hoverCloseDelay: { type: Number, default: 300 },
    hoverOpenDelay: { type: Number, default: 0 },
    contentClass: { type: String, default: '' },
    closeOnContentClick: { type: Boolean, default: false },
    activatorExtraClass: { type: [String, Array, Object], default: '' },
    activatorActiveClass: { type: [String, Array, Object], default: '' },
    activatorAriaLabel: { type: String, default: null },
    label: { type: String, default: 'Apps' },
    iconSrc: { type: String, default: '/img/navicon/Apps.svg' },
    iconAlt: { type: String, default: 'Apps' },
    applist: { type: Array, default: () => [] },
    isAppActive: { type: Function, default: () => false },
    labelClass: { type: String, default: '' },
    selectedItemClass: { type: String, default: 'radius-small selected-menu-item' },
    showShortcutIcon: { type: Boolean, default: false },
    showTrailingShortcutIcon: { type: Boolean, default: false },
    shortcutIconSrc: { type: String, default: '/img/navicon/Shortcuts.svg' },
    shortcutIconAlt: { type: String, default: 'Shortcut' },
    shortcutIconClass: { type: String, default: 'ml-2 nav-img-icon cursor-pointer' },
    trailingIconClass: { type: String, default: 'ml-2 nav-img-icon' },
    shortcutLoadingFn: { type: Function, default: null },
    testIdPrefix: { type: String, required: true }
  },
  computed: {
    keySuffix() {
      return this.label.toLowerCase().replace(/\s+/g, '-');
    },
    hasApps() {
      return Array.isArray(this.applist) && this.applist.length > 0;
    },
    activatorClasses() {
      const base = 'd-flex align-center nav-standard-item cursor-pointer menu-trigger-item';
      const classes = [base];
      if (this.activatorExtraClass) classes.push(this.activatorExtraClass);
      if (this.isAnyAppActive && this.activatorActiveClass) {
        classes.push(this.activatorActiveClass);
      }
      return classes;
    },
    isAnyAppActive() {
      if (!this.hasApps) return false;
      try {
        return this.applist.some(app => this.isAppActive(app));
      } catch (e) {
        return false;
      }
    }
  },
  methods: {
    displayName(app) {
      return app?.displayname || app?.name || '';
    },
    listItemClasses(subItem) {
      const base = ['subscriptions-menu-item'];
      try {
        if (this.isAppActive(subItem)) {
          base.push(this.selectedItemClass);
        }
      } catch (e) {
        /* no-op */
      }
      return base;
    },
    handleAppClick(app) {
      this.$emit('app-click', app);
    },
    isShortcutLoading(app) {
      if (!this.showShortcutIcon || !this.shortcutLoadingFn) return false;
      try {
        return this.shortcutLoadingFn(app);
      } catch (e) {
        return false;
      }
    },
    handleAddShortcut(app) {
      this.$emit('add-shortcut', app);
    }
  }
};
</script>
